import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  isDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { Store } from '@blocksuite/store';

export type RelationRowsWindowRequest = {
  offset?: number;
  limit: number;
};

export type RelationRowInfo = {
  id: string;
  display: string;
};

export type RelationRowsWindow = {
  offset: number;
  limit: number;
  totalCount: number;
  hasMore: boolean;
  rows: RelationRowInfo[];
};

const dataSourcesByModel = new WeakMap<
  DatabaseBlockModel,
  DatabaseBlockDataSource
>();

export type RelationRowDataLoadState = 'loading' | 'ready';

type RelationRowDataSource = DatabaseBlockDataSource & {
  ensureRelationRowDataLoaded?: () => void;
  getRelationRowDataLoadState?: () => RelationRowDataLoadState;
};

export function formatRelationDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'string') return value || '(empty)';
  if (Array.isArray(value)) return value.join(', ') || '(empty)';
  if (typeof value === 'object' && 'toString' in value) {
    return value.toString() || '(empty)';
  }
  return String(value);
}

export type DatabaseStorageState = 'missing' | 'loading' | 'ready';

export function findDatabaseBlockModel(
  store: Store,
  databaseId: string
): DatabaseBlockModel | undefined {
  const databases = store.getBlocksByFlavour('affine:database');
  const targetDb = databases.find(db => db.id === databaseId);

  if (!targetDb) {
    return;
  }

  return ((targetDb as unknown as { model?: DatabaseBlockModel }).model ??
    targetDb) as DatabaseBlockModel;
}

export function getDatabaseStorageState(
  store: Store,
  databaseId: string
): DatabaseStorageState {
  const model = findDatabaseBlockModel(store, databaseId);
  if (!model) {
    return 'missing';
  }
  return isDatabaseStorageModelReady(model) ? 'ready' : 'loading';
}

export function getDatabaseDataSource(
  store: Store,
  databaseId: string
): DatabaseBlockDataSource | undefined {
  const model = findDatabaseBlockModel(store, databaseId);

  // Constructing a DataSource before the sharded root doc is synced throws
  // inside resolveDatabaseStorageModel — callers must treat undefined as
  // "not available (yet)" and re-check via observeDatabaseStorageModelReady.
  if (!model || !isDatabaseStorageModelReady(model)) {
    return;
  }

  let dataSource = dataSourcesByModel.get(model);
  if (!dataSource) {
    dataSource = createDatabaseBlockDataSource(model);
    dataSourcesByModel.set(model, dataSource);
  }
  return dataSource;
}

export function ensureRelationRowDataLoaded(
  dataSource: DatabaseBlockDataSource
): void {
  (dataSource as RelationRowDataSource).ensureRelationRowDataLoaded?.();
}

export function getRelationRowDataLoadState(
  dataSource: DatabaseBlockDataSource
): RelationRowDataLoadState {
  return (
    (dataSource as RelationRowDataSource).getRelationRowDataLoadState?.() ??
    'ready'
  );
}

export function getDatabaseRowsWindow(
  dataSource: DatabaseBlockDataSource,
  displayFieldId: string,
  request: RelationRowsWindowRequest
): RelationRowsWindow {
  const rowWindow = dataSource.getRowsWindow(request);
  return {
    offset: rowWindow.offset,
    limit: rowWindow.limit,
    totalCount: rowWindow.totalCount,
    hasMore: rowWindow.hasMore,
    rows: rowWindow.rowIds.map(rowId => ({
      id: rowId,
      display: formatRelationDisplayValue(
        dataSource.cellValueGet(rowId, displayFieldId)
      ),
    })),
  };
}

export function rowExistsInDataSource(
  dataSource: DatabaseBlockDataSource,
  rowId: string
): boolean {
  return dataSource.rowExists(rowId);
}

export function getRowDisplayValueFromDataSource(
  dataSource: DatabaseBlockDataSource,
  rowId: string,
  displayFieldId: string
): string | undefined {
  if (!rowExistsInDataSource(dataSource, rowId)) {
    return;
  }
  return formatRelationDisplayValue(
    dataSource.cellValueGet(rowId, displayFieldId)
  );
}
